import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const LINKS = [
  { label: "Home", path: "/", testid: "nav-home-link" },
  { label: "Creator", path: "/creator", testid: "nav-creator-link" },
  { label: "Lavora con noi", path: "/lavora-con-noi", testid: "nav-lavora-link" },
  { label: "Metodo & Privacy", path: "/metodo-privacy", testid: "nav-metodo-link" },
  { label: "Chi siamo", path: "/chi-siamo", testid: "nav-chi-siamo-link" },
  { label: "Partnership", path: "/partnership-dopamine", testid: "nav-partnership-link" },
  { label: "FAQ", path: "/faq", testid: "nav-faq-link" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openContact, settings } = useSite();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-[#1A1A1A] bg-[#080808]/80 backdrop-blur-xl"
            : "border-b border-transparent bg-[#080808]/50 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex h-20 lg:h-24 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" className="flex items-center gap-3.5 lg:gap-4" data-testid="nav-logo-link">
            <img
              src="/assets/iem-emblem.png"
              alt="IEM — Iconic Elite Management"
              className="h-11 w-11 xl:h-12 xl:w-12 2xl:h-14 2xl:w-14 rounded-full ring-1 ring-gold/25"
            />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-xl lg:text-2xl 2xl:text-3xl tracking-[0.18em] text-[#F5F3EE]">IEM</span>
              <span className="mt-1.5 whitespace-nowrap text-[8px] sm:text-[9px] tracking-[0.28em] text-gold/90 uppercase">
                Iconic Elite Management
              </span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-8" data-testid="nav-desktop-menu">
            {LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[13px] 2xl:text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-[#969696] hover:text-[#F5F3EE]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <a
              data-testid="nav-whatsapp-mobile"
              href={settings.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contatto diretto WhatsApp"
              className="p-2.5 text-gold transition-colors hover:text-gold-bright xl:hidden"
            >
              <MessageCircle size={22} />
            </a>
            <button
              data-testid="nav-become-creator-cta"
              onClick={() => openContact("creator")}
              className="hidden sm:inline-flex items-center gap-2.5 whitespace-nowrap border border-gold/60 bg-gold/10 px-5 py-3 text-[12px] 2xl:px-7 2xl:py-3.5 2xl:text-[13px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-[#080808]"
            >
              <MessageCircle size={15} /> Candidati come Creator
            </button>
            <button
              data-testid="nav-mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className="xl:hidden p-3 text-[#F5F3EE]"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#080808]/97 backdrop-blur-2xl px-8 xl:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-3xl sm:text-4xl transition-colors ${
                        isActive ? "text-gold" : "text-[#F5F3EE]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              data-testid="mobile-become-creator-cta"
              onClick={() => openContact("creator")}
              className="mt-10 flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold"
            >
              <MessageCircle size={16} /> Candidati come Creator
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
